import * as React from 'react';
import AppTabs from '../../../components/AppTabs';
import { FC, useEffect, useState, useCallback, useRef } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, Alert } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { useDispatch, useSelector } from 'react-redux';
import { alertError, alertGenericError, alertSuccess } from '../../../store/alerts/actions';
import {
    getGoogleCalendarStatusAsync,
    getGoogleCalendarAuthUrlAsync,
    connectGoogleCalendarAsync,
    disconnectGoogleCalendarAsync,
    testGoogleReviewSmsAsync,
    testGoogleReviewEmailAsync,
} from '../../../ajax/requests';
import { getErrorMessage, isConflictError } from '../../../ajax/errors';
import { useLocation, useHistory } from 'react-router-dom';
import { push } from 'connected-react-router';
import { paths } from '../../../app/constants';
import Typography from '../../../components/Typography';
import styled from 'styled-components';
import { Tabs, Tab } from '@material-ui/core';
import { TextInputField, ComboField } from '../../../components/Forms/fields';
import { getGeneralSettings } from '../../../store/settings/selectors';
import { updateGeneralSettings, updateIntegrationsSettings } from '../../../store/settings/actions';
import {
    ChannelCard, ChannelHeader, ChannelInfo, ChannelContent, ChannelStatus,
    TestSection, TestActionButton, getMessageIcon,
} from '../RemindersSettings/remindersShared';
import { ApplicationState } from '../../../store';
import { IntegrationsSettingsData } from '../../../models';
import { hasGoogleReviewUrlForTest, isMultiBranchGoogleReviewSetup } from '../../../shared/googleReviewBranches';
import AppButton from '../../../components/AppButton';

const HtmlEditor = React.lazy(() => import('../../Campaigns/HtmlEditor').then(m => ({ default: m.HtmlEditor })));

// ── Shared styled components ──────────────────────────────────────────────────

const Card = styled.div`
    display: flex;
    flex-direction: column;
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 8px;
    overflow: hidden;
    width: 100%;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    padding: var(--spacer-S) var(--spacer-M);
    background: var(--bg-light);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
`;

const ServiceName = styled.div`
    display: flex;
    flex-direction: column;
`;

const ServiceTitle = styled.span`
    font-weight: 600;
    font-size: 1rem;
    color: var(--text-body);
`;

const ServiceSubtitle = styled.span`
    font-size: 0.8rem;
    color: var(--text-muted);
`;

const CardBody = styled.div`
    padding: var(--spacer-S) var(--spacer-M);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const StatusRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const StatusDot = styled.div<{ connected: boolean }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background-color: ${p => p.connected ? 'var(--success-color)' : 'var(--appLigthGrey)'};
`;

const SectionDivider = styled.div`
    border-top: var(--border-thin) solid var(--appLigthGrey);
    margin: var(--spacer-XXS) 0;
`;

// ── Icons ─────────────────────────────────────────────────────────────────────

const GoogleCalendarIcon: FC = () => (
    <img
        src="https://www.gstatic.com/images/branding/product/2x/calendar_48dp.png"
        alt="Google Calendar"
        width={56}
        height={56}
    />
);

const GoogleReviewsIcon: FC = () => (
    <svg width={56} height={56} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
        <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
        <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
        <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
        <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
);

// ── Default template values ───────────────────────────────────────────────────

const WHATSAPP_TEMPLATE_PREVIEW = "Olá {{client_name}}!\n\nA {{company_name}} agradece a sua visita. Se tiver 30 segundos, gostaríamos muito de conhecer a sua opinião.\n\nPode deixar a sua avaliação aqui:\n{{review_url}}\n\nObrigado!";

// ── Google Calendar card ──────────────────────────────────────────────────────

interface GoogleCalendarCardProps {
    id: string;
    showNote?: boolean;
}

export const GoogleCalendarCard: FC<GoogleCalendarCardProps> = ({ id, showNote }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const [isConnected, setIsConnected] = useState(false);
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

    const redirectUri = `${window.location.origin}${paths.profile()}`;

    const fetchStatus = useCallback(async () => {
        try {
            const { isConnected: connected } = await getGoogleCalendarStatusAsync();
            setIsConnected(connected);
        } catch {
            // silently ignore — status will remain as false
        } finally {
            setLoading(false);
        }
    }, []);

    const handleOAuthCallback = useCallback(async (code: string) => {
        setConnecting(true);
        history.replace(`${paths.settings()}/google-calendar`);
        try {
            await connectGoogleCalendarAsync(code, redirectUri);
            setIsConnected(true);
            dispatch(alertSuccess(t(TK.googleCalendarConnectSuccess)));
        } catch {
            dispatch(alertError(t(TK.googleCalendarConnectError)));
        } finally {
            setConnecting(false);
        }
    }, [dispatch, history, redirectUri, t]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        if (code) {
            handleOAuthCallback(code);
        } else {
            fetchStatus();
        }
    }, []);

    const handleConnect = async () => {
        setConnecting(true);
        try {
            const { authUrl } = await getGoogleCalendarAuthUrlAsync(redirectUri);
            window.location.href = authUrl;
        } catch {
            dispatch(alertError(t(TK.googleCalendarConnectError)));
            setConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        setShowDisconnectConfirm(false);
        setDisconnecting(true);
        try {
            await disconnectGoogleCalendarAsync();
            setIsConnected(false);
            dispatch(alertSuccess(t(TK.googleCalendarDisconnect)));
        } catch {
            dispatch(alertError(t(TK.googleCalendarDisconnectError)));
        } finally {
            setDisconnecting(false);
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <Card>
                <CardHeader>
                    <GoogleCalendarIcon />
                    <ServiceName>
                        <ServiceTitle>Google Calendar</ServiceTitle>
                        <ServiceSubtitle>{t(TK.googleCalendarDescription)}</ServiceSubtitle>
                    </ServiceName>
                </CardHeader>

                <CardBody>
                    {showNote && (
                        <Alert color="info" style={{ marginBottom: 0 }}>
                            {t(TK.googleCalendarProfessionalNote)}
                        </Alert>
                    )}

                    {!loading && (
                        <StatusRow>
                            <StatusDot connected={isConnected} />
                            <Typography size="medium">
                                {isConnected ? t(TK.googleCalendarConnected) : t(TK.googleCalendarNotConnected)}
                            </Typography>
                        </StatusRow>
                    )}

                    {!isConnected ? (
                        <AppButton
                            id={`${id}-connect-button`}
                            color="primary"
                            disabled={connecting || loading}
                            onClick={handleConnect}
                            style={{ alignSelf: 'flex-start' }}
                        >
                            {connecting ? '...' : t(TK.googleCalendarConnect)}
                        </AppButton>
                    ) : (
                        <AppButton
                            id={`${id}-disconnect-button`}
                            color="danger"
                            outline
                            disabled={disconnecting}
                            onClick={() => setShowDisconnectConfirm(true)}
                            style={{ alignSelf: 'flex-start' }}
                        >
                            {t(TK.googleCalendarDisconnect)}
                        </AppButton>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={showDisconnectConfirm} toggle={() => setShowDisconnectConfirm(false)}>
                <ModalHeader toggle={() => setShowDisconnectConfirm(false)}>
                    {t(TK.googleCalendarDisconnectConfirmTitle)}
                </ModalHeader>
                <ModalBody>
                    {t(TK.googleCalendarDisconnectConfirmBody)}
                </ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={() => setShowDisconnectConfirm(false)}>
                        {t(TK.cancel)}
                    </AppButton>
                    <AppButton id={`${id}-disconnect-confirm-button`} color="danger" onClick={handleDisconnect}>
                        {t(TK.googleCalendarDisconnect)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </div>
    );
};

// ── Google Reviews tab ────────────────────────────────────────────────────────

interface GoogleReviewsTabProps {
    id: string;
}

export const GoogleReviewsTab: FC<GoogleReviewsTabProps> = ({ id }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useSelector(getGeneralSettings);
    const integrations = useSelector((state: ApplicationState): IntegrationsSettingsData => state.settings.integrations);

    const [googleReviewUrl, setGoogleReviewUrl] = useState('');
    const [branchReviewUrls, setBranchReviewUrls] = useState<Record<string, string>>({});
    const [smsTemplate, setSmsTemplate] = useState('');
    const [emailBodyText, setEmailBodyText] = useState('');
    const [emailTest, setEmailTest] = useState('');
    const [phoneTest, setPhoneTest] = useState('');
    const [testing, setTesting] = useState(false);
    const [expandedChannels, setExpandedChannels] = useState<Set<'sms' | 'email' | 'whatsapp'>>(new Set());
    const [testBranchId, setTestBranchId] = useState<string | undefined>(undefined);

    const hasBranches = Object.keys(generalSettings.branches ?? {}).length > 0;
    const isMultiBranch = isMultiBranchGoogleReviewSetup(generalSettings.branches);
    const branchTestOptions = React.useMemo(() => Object.entries(generalSettings.branches ?? {})
        .map(([key, branch]) => ({ value: key, label: branch.displayName })),
    [generalSettings.branches]);
    const resolvedTestBranchId = hasBranches
        ? (testBranchId
            ?? Object.entries(branchReviewUrls).find(([, url]) => url.trim())?.[0]
            ?? Object.keys(generalSettings.branches ?? {})[0])
        : undefined;
    const resolvedTestBranchHasUrl = hasGoogleReviewUrlForTest({
        isMultiBranch,
        globalGoogleReviewUrl: googleReviewUrl,
        branchId: resolvedTestBranchId,
        branchReviewUrls,
    });

    const initialized = useRef(false);
    useEffect(() => {
        if (!initialized.current && generalSettings.companyName !== undefined) {
            initialized.current = true;
            setGoogleReviewUrl(generalSettings.googleReviewUrl ?? '');
            setBranchReviewUrls(Object.fromEntries(
                Object.entries(generalSettings.branches ?? {}).map(([key, branch]) => [key, branch.googleReviewUrl ?? ''])
            ));
            setSmsTemplate(integrations?.google?.googleReview?.smsTemplate || t(TK.googleReviewSmsDefaultTemplate));
            setEmailBodyText(integrations?.google?.googleReview?.emailBodyText || t(TK.googleReviewEmailDefaultTemplate));
        }
    }, [generalSettings, integrations]);

    const toggleChannel = useCallback((channel: 'sms' | 'email' | 'whatsapp') => {
        setExpandedChannels(prev => {
            const next = new Set(prev);
            if (next.has(channel)) next.delete(channel);
            else next.add(channel);
            return next;
        });
    }, []);

    const catchRequestError = useCallback((error: unknown) => {
        const message = getErrorMessage(error);
        if (message) {
            dispatch(alertError(message));
            return;
        }
        dispatch(isConflictError(error)
            ? alertError(t(TK.plafontExausted))
            : alertGenericError());
    }, [dispatch, t]);

    const handleSave = useCallback(() => {
        dispatch(updateGeneralSettings({
            ...generalSettings,
            googleReviewUrl: isMultiBranch ? generalSettings.googleReviewUrl : (googleReviewUrl.trim() || undefined),
            branches: hasBranches
                ? Object.fromEntries(
                    Object.entries(generalSettings.branches ?? {}).map(([key, branch]) => [
                        key,
                        { ...branch, googleReviewUrl: branchReviewUrls[key]?.trim() || undefined },
                    ])
                )
                : generalSettings.branches,
        }));
        dispatch(updateIntegrationsSettings({
            ...integrations,
            google: {
                ...integrations?.google,
                googleReview: {
                    smsTemplate: smsTemplate.trim() || undefined,
                    emailBodyText: emailBodyText.trim() || undefined,
                },
            },
        }));
    }, [dispatch, generalSettings, integrations, googleReviewUrl, branchReviewUrls, hasBranches, smsTemplate, emailBodyText]);

    const handleTestSms = useCallback(() => {
        const hasUrl = resolvedTestBranchHasUrl;
        if (!hasUrl) {
            dispatch(alertError(t(TK.googleReviewUrlRequired)));
            return;
        }
        if (!/^((\+\d{12})|(\d{9}))$/.test(phoneTest)) {
            dispatch(alertError(t(TK.invalidPhoneNumber)));
            return;
        }
        setTesting(true);
        testGoogleReviewSmsAsync({
            receiverEmailOrPhoneNumber: phoneTest,
            smsTemplate: smsTemplate.trim() || undefined,
            branchId: resolvedTestBranchId,
        })
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchRequestError)
            .finally(() => setTesting(false));
    }, [googleReviewUrl, hasBranches, resolvedTestBranchHasUrl, resolvedTestBranchId, phoneTest, smsTemplate, dispatch, catchRequestError, t]);

    const handleTestEmail = useCallback(() => {
        const hasUrl = resolvedTestBranchHasUrl;
        if (!hasUrl) {
            dispatch(alertError(t(TK.googleReviewUrlRequired)));
            return;
        }
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(emailTest)) {
            dispatch(alertError(t(TK.invalidEmail)));
            return;
        }
        setTesting(true);
        testGoogleReviewEmailAsync({
            receiverEmailOrPhoneNumber: emailTest,
            emailBodyText: emailBodyText.trim() || undefined,
            branchId: resolvedTestBranchId,
        })
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchRequestError)
            .finally(() => setTesting(false));
    }, [googleReviewUrl, hasBranches, resolvedTestBranchHasUrl, resolvedTestBranchId, emailTest, emailBodyText, dispatch, catchRequestError, t]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', width: '100%' }}>
            <Card>
                <CardHeader>
                    <GoogleReviewsIcon />
                    <ServiceName>
                        <ServiceTitle>{t(TK.googleReviewsSettingsTitle)}</ServiceTitle>
                        <ServiceSubtitle>{t(TK.googleReviewsDescription)}</ServiceSubtitle>
                    </ServiceName>
                    <AppButton
                        color="secondary"
                        outline
                        size="sm"
                        onClick={() => dispatch(push(`${paths.analytics()}?tab=googleReviews`))}
                        style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
                    >
                        {t(TK.googleReviewsViewAnalytics)}
                    </AppButton>
                </CardHeader>

                <CardBody>
                    {isMultiBranch ? (
                        Object.entries(generalSettings.branches ?? {}).map(([key, branch]) => (
                            <TextInputField
                                key={key}
                                id={`${id}-branch-${key}-review-url`}
                                label={`${branch.displayName} — ${t(TK.googleReviewUrl)}`}
                                helpText={t(TK.googleReviewBranchUrlHelp)}
                                value={branchReviewUrls[key] ?? ''}
                                onChange={url => setBranchReviewUrls(prev => ({ ...prev, [key]: url }))}
                                placeholder="https://g.page/r/..."
                                type="url"
                            />
                        ))
                    ) : (
                        <TextInputField
                            id={`${id}-review-url`}
                            label={t(TK.googleReviewUrl)}
                            helpText={t(TK.googleReviewUrlHelp)}
                            value={googleReviewUrl}
                            onChange={setGoogleReviewUrl}
                            placeholder="https://g.page/r/..."
                            type="url"
                        />
                    )}

                    <Alert color="info" style={{ marginBottom: 0 }}>
                        {t(TK.googleReviewsTriggerHint)}
                    </Alert>

                    <SectionDivider />

                    {/* SMS channel */}
                    <ChannelCard $isActive={expandedChannels.has('sms')} $isSelected={expandedChannels.has('sms')}>
                        <ChannelHeader
                            $isActive={expandedChannels.has('sms')}
                            $isSelected={expandedChannels.has('sms')}
                            onClick={() => toggleChannel('sms')}
                        >
                            <ChannelInfo>
                                {getMessageIcon('sms')}
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
                                        <Typography><b>SMS</b></Typography>
                                        <ChannelStatus $isActive={true}>{t(TK.active)}</ChannelStatus>
                                    </div>
                                    <Typography size="smaller" style={{ color: 'var(--text-muted)' }}>
                                        {t(TK.googleReviewChannelEditableSubtitle)}
                                    </Typography>
                                </div>
                            </ChannelInfo>
                        </ChannelHeader>
                        {expandedChannels.has('sms') && (
                            <ChannelContent>
                                <TextInputField
                                    id={`${id}-sms-template`}
                                    label={t(TK.googleReviewSmsTemplateLabel)}
                                    helpText={t(TK.googleReviewSmsTemplatePlaceholders)}
                                    value={smsTemplate}
                                    onChange={setSmsTemplate}
                                    type="textarea"
                                    maxLength={320}
                                />
                                {isMultiBranch && (
                                    <ComboField
                                        id={`${id}-test-sms-branch`}
                                        label={t(TK.googleReviewSelectBranchPlaceholder)}
                                        value={resolvedTestBranchId ?? ''}
                                        options={branchTestOptions}
                                        onChange={value => setTestBranchId(value || undefined)}
                                    />
                                )}
                                <TestSection>
                                    <TextInputField
                                        style={{ flexGrow: 1, minWidth: '200px' }}
                                        label=""
                                        value={phoneTest}
                                        onChange={setPhoneTest}
                                        placeholder="+351912345678"
                                    />
                                    <TestActionButton
                                        id={`${id}-test-sms-button`}
                                        disabled={testing}
                                        onClick={handleTestSms}
                                    >
                                        {getMessageIcon('sms', { style: { marginRight: 'var(--spacer-XXS)' } })}
                                        {t(TK.testSms)}
                                    </TestActionButton>
                                </TestSection>
                            </ChannelContent>
                        )}
                    </ChannelCard>

                    {/* Email channel */}
                    <ChannelCard $isActive={expandedChannels.has('email')} $isSelected={expandedChannels.has('email')}>
                        <ChannelHeader
                            $isActive={expandedChannels.has('email')}
                            $isSelected={expandedChannels.has('email')}
                            onClick={() => toggleChannel('email')}
                        >
                            <ChannelInfo>
                                {getMessageIcon('email')}
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
                                        <Typography><b>Email</b></Typography>
                                        <ChannelStatus $isActive={true}>{t(TK.active)}</ChannelStatus>
                                    </div>
                                    <Typography size="smaller" style={{ color: 'var(--text-muted)' }}>
                                        {t(TK.googleReviewChannelEditableSubtitle)}
                                    </Typography>
                                </div>
                            </ChannelInfo>
                        </ChannelHeader>
                        {expandedChannels.has('email') && (
                            <ChannelContent>
                                <Typography size="smaller" style={{ color: 'var(--text-muted)' }}>
                                    {t(TK.googleReviewEmailBodyNote)}
                                </Typography>
                                <React.Suspense fallback={<div style={{ padding: 'var(--spacer-M)', textAlign: 'center' }}>Loading editor...</div>}>
                                    <HtmlEditor
                                        value={emailBodyText}
                                        onChange={setEmailBodyText}
                                    />
                                </React.Suspense>
                                {isMultiBranch && (
                                    <ComboField
                                        id={`${id}-test-email-branch`}
                                        label={t(TK.googleReviewSelectBranchPlaceholder)}
                                        value={resolvedTestBranchId ?? ''}
                                        options={branchTestOptions}
                                        onChange={value => setTestBranchId(value || undefined)}
                                    />
                                )}
                                <TestSection>
                                    <TextInputField
                                        style={{ flexGrow: 1, minWidth: '200px' }}
                                        label=""
                                        value={emailTest}
                                        onChange={setEmailTest}
                                        placeholder="xpto@domain.com"
                                    />
                                    <TestActionButton
                                        id={`${id}-test-email-button`}
                                        disabled={testing}
                                        onClick={handleTestEmail}
                                    >
                                        {getMessageIcon('email', { style: { marginRight: 'var(--spacer-XXS)' } })}
                                        {t(TK.testEmail)}
                                    </TestActionButton>
                                </TestSection>
                            </ChannelContent>
                        )}
                    </ChannelCard>

                    {/* WhatsApp channel (read-only Meta template) */}
                    <ChannelCard $isActive={expandedChannels.has('whatsapp')} $isSelected={expandedChannels.has('whatsapp')}>
                        <ChannelHeader
                            $isActive={expandedChannels.has('whatsapp')}
                            $isSelected={expandedChannels.has('whatsapp')}
                            onClick={() => toggleChannel('whatsapp')}
                        >
                            <ChannelInfo>
                                {getMessageIcon('whatsapp')}
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
                                        <Typography><b>WhatsApp</b></Typography>
                                        <ChannelStatus $isActive={true}>{t(TK.active)}</ChannelStatus>
                                    </div>
                                    <Typography size="smaller" style={{ color: 'var(--text-muted)' }}>
                                        {t(TK.googleReviewWhatsAppChannelSubtitle)}
                                    </Typography>
                                </div>
                            </ChannelInfo>
                        </ChannelHeader>
                        {expandedChannels.has('whatsapp') && (
                            <ChannelContent>
                                <Typography size="smaller" style={{ color: 'var(--text-muted)' }}>
                                    {t(TK.googleReviewWhatsAppMetaNote)}
                                </Typography>
                                <TextInputField
                                    id={`${id}-whatsapp-preview`}
                                    label={t(TK.googleReviewWhatsAppPreviewLabel)}
                                    value={WHATSAPP_TEMPLATE_PREVIEW}
                                    onChange={() => {}}
                                    type="textarea"
                                    disabled
                                />
                            </ChannelContent>
                        )}
                    </ChannelCard>

                    <SectionDivider />

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <AppButton
                            id={`${id}-save-button`}
                            color="primary"
                            onClick={handleSave}
                        >
                            {t(TK.save)}
                        </AppButton>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

// ── Main exported component ───────────────────────────────────────────────────

export const GoogleCalendarSettings: FC<{ id: string }> = ({ id }) => {
    const t = useTranslations();
    const [tab, setTab] = useState<'calendar' | 'reviews'>('calendar');

    return (
        <div style={{ width: '100%' }}>
            <AppTabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary" style={{ marginBottom: 'var(--spacer-S)', borderBottom: 'var(--border-thin) solid var(--appLigthGrey)' }}>
                <Tab label={t(TK.googleCalendarSettingsTitle)} value="calendar" />
                <Tab label={t(TK.googleReviewsSettingsTitle)} value="reviews" />
            </AppTabs>

            <div>
                {tab === 'calendar' && <GoogleCalendarCard id={id} showNote />}
                {tab === 'reviews' && <GoogleReviewsTab id={id} />}
            </div>
        </div>
    );
};
